import { useEffect, useMemo, useState } from "react";
import Card, { SectionTitle } from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import StatCard from "@/components/base/StatCard";
import Avatar from "@/components/base/Avatar";
import { formatMoney, plural } from "@/utils/format";
import { usePager } from "@/hooks/usePager";
import Pagination from "@/components/base/Pagination";
import { isRated, monthlySalary, WEEKS_PER_MONTH } from "@/utils/payroll";
import type { Cycle, Teacher } from "@/types";
import PayrollPrintModal from "@/pages/dashboard/teachers/components/PayrollPrintModal";
import { cycleIdOf } from "@/utils/cycleScope";
import { currencySymbol } from "@/utils/currency";

/* ------------------------------------------------------------------------- */
/* Champ numérique éditable (validation à la sortie du champ)                 */
/* ------------------------------------------------------------------------- */

interface NumberCellProps {
  value?: number;
  onCommit: (value: number) => void;
  placeholder?: string;
  suffix?: string;
}

function NumberCell({ value, onCommit, placeholder = "0", suffix }: NumberCellProps) {
  const [draft, setDraft] = useState(value ? String(value) : "");

  useEffect(() => {
    setDraft(value ? String(value) : "");
  }, [value]);

  const commit = () => {
    const parsed = Math.max(0, Math.round(Number(draft.replace(/[^0-9]/g, "")) || 0));
    if (parsed !== (value ?? 0)) onCommit(parsed);
    setDraft(parsed ? String(parsed) : "");
  };

  return (
    <div className="relative inline-flex items-center">
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
        }}
        className={`w-24 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 ${
          suffix ? "pr-9" : ""
        }`}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-2.5 text-xs text-foreground-400">
          {suffix}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* Composant principal                                                        */
/* ------------------------------------------------------------------------- */

interface TeacherHoursTabProps {
  teachers: Teacher[];
  cycles: Cycle[];
  onUpdate: (id: string, patch: Partial<Pick<Teacher, "weeklyHours" | "hourlyRate">>) => void;
}

export default function TeacherHoursTab({ teachers, cycles, onUpdate }: TeacherHoursTabProps) {
  const PRIMARY_CYCLE_ID = cycleIdOf(cycles, "PRIMAIRE");
  const cycleLabels = Object.fromEntries(cycles.map((cycle) => [cycle.id, cycle.label])) as Record<string, string>;
  const [search, setSearch] = useState("");
  const [cycleFilter, setCycleFilter] = useState("all");
  const [printOpen, setPrintOpen] = useState(false);
  const pager = usePager();

  const term = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      teachers.filter((teacher) => {
        const matchCycle = cycleFilter === "all" || teacher.cycleId === cycleFilter;
        const matchTerm =
          !term ||
          teacher.firstName.toLowerCase().includes(term) ||
          teacher.lastName.toLowerCase().includes(term);
        return matchCycle && matchTerm;
      }),
    [teachers, cycleFilter, term],
  );

  const page = pager.slice(rows, `${cycleFilter}|${term}`);
  const totalWeeklyHours = rows.reduce((sum, t) => sum + (t.weeklyHours ?? 0), 0);
  const payroll = rows.reduce((sum, t) => sum + monthlySalary(t), 0);
  const configured = rows.filter(isRated).length;
  const rated = rows.filter((t) => !t.fixedMonthlySalary && (t.hourlyRate ?? 0) > 0);
  const averageRate = rated.length
    ? Math.round(rated.reduce((sum, t) => sum + (t.hourlyRate ?? 0), 0) / rated.length)
    : 0;

  const cycleSummary = cycles.map((cycle) => {
    const list = rows.filter((t) => t.cycleId === cycle.id);
    return {
      id: cycle.id,
      label: cycle.label,
      count: list.length,
      hours: list.reduce((sum, t) => sum + (t.weeklyHours ?? 0), 0),
      payroll: list.reduce((sum, t) => sum + monthlySalary(t), 0),
    };
  });

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50/60 px-4 py-3">
        <span className="mt-0.5 flex h-4 w-4 items-center justify-center text-primary-700">
          <i className="ri-information-line text-base"></i>
        </span>
        <p className="text-xs text-foreground-700">
          Saisissez les <strong>heures par semaine</strong> et le <strong>tarif horaire</strong> de
          chaque enseignant — primaire comme secondaire. Le <strong>salaire mensuel</strong> se
          calcule automatiquement : heures / semaine × tarif horaire × {WEEKS_PER_MONTH} semaines.
          Au primaire, un salaire fixe mensuel peut remplacer ce calcul (fiche de l'enseignant).
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Masse salariale mensuelle"
          value={formatMoney(payroll)}
          icon="ri-wallet-3-line"
          tone="primary"
          hint={`${plural(configured, "enseignant configuré", "enseignants configurés")}`}
        />
        <StatCard
          label="Heures / semaine"
          value={`${totalWeeklyHours} h`}
          icon="ri-timer-line"
          tone="accent"
          hint={`${plural(rows.length, "enseignant")}`}
        />
        <StatCard
          label="Tarif horaire moyen"
          value={formatMoney(averageRate)}
          icon="ri-money-dollar-circle-line"
          tone="secondary"
        />
        <StatCard
          label="À compléter"
          value={String(rows.length - configured)}
          icon="ri-error-warning-line"
          tone="accent"
          hint="heures ou tarif manquants"
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-sm"></i>
          </span>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un enseignant..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="relative sm:w-56">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-filter-line text-sm"></i>
          </span>
          <select
            value={cycleFilter}
            onChange={(event) => setCycleFilter(event.target.value)}
            className="w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-9 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Tous les cycles</option>
            {cycles.map((cycle) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.label}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-arrow-down-s-line text-base"></i>
          </span>
        </div>
        <Button variant="outline" icon="ri-printer-line" onClick={() => setPrintOpen(true)}>
          Fiche de paie
        </Button>
      </div>

      <Card padded={false}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-background-200 bg-background-50/60 text-left text-xs uppercase tracking-wider text-foreground-500">
                <th className="px-5 py-3 font-medium">Enseignant</th>
                <th className="px-5 py-3 font-medium">Cycle</th>
                <th className="px-5 py-3 font-medium">Heures / semaine</th>
                <th className="px-5 py-3 font-medium">Tarif horaire</th>
                <th className="px-5 py-3 text-right font-medium">Salaire mensuel</th>
              </tr>
            </thead>
            <tbody>
              {page.items.map((teacher) => {
                const isPrimary = teacher.cycleId === PRIMARY_CYCLE_ID;
                const salary = monthlySalary(teacher);
                const complete = isRated(teacher);
                return (
                  <tr
                    key={teacher.id}
                    className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <Avatar lastName={teacher.lastName} firstName={teacher.firstName} size="sm" />
                        <div>
                          <p className="text-sm font-medium text-foreground-900">
                            {teacher.firstName} {teacher.lastName}
                          </p>
                          <p className="text-xs text-foreground-500">{teacher.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge tone={isPrimary ? "warning" : "secondary"}>
                        {cycleLabels[teacher.cycleId] ?? "—"}
                      </Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <NumberCell
                        value={teacher.weeklyHours}
                        suffix="h"
                        onCommit={(value) => onUpdate(teacher.id, { weeklyHours: value })}
                      />
                    </td>
                    <td className="px-5 py-3.5">
                      {teacher.fixedMonthlySalary ? (
                        <span className="text-xs text-foreground-500">Salaire fixe</span>
                      ) : (
                        <NumberCell
                          value={teacher.hourlyRate}
                          suffix={currencySymbol()}
                          onCommit={(value) => onUpdate(teacher.id, { hourlyRate: value })}
                        />
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {complete ? (
                        <div>
                          <p className="text-sm font-semibold text-foreground-950">{formatMoney(salary)}</p>
                          <p className="text-xs text-foreground-400">
                            {teacher.fixedMonthlySalary
                              ? "Salaire fixe mensuel"
                              : `${teacher.weeklyHours} h × ${formatMoney(teacher.hourlyRate ?? 0)} × ${WEEKS_PER_MONTH} sem.`}
                          </p>
                        </div>
                      ) : (
                        <span className="text-xs text-foreground-400">À compléter</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-sm text-foreground-400">
                    Aucun enseignant ne correspond à votre recherche.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-background-200 bg-background-100/60">
                <td className="px-5 py-3 font-medium text-foreground-900" colSpan={2}>
                  Masse salariale ({rows.length} enseignants)
                </td>
                <td className="px-5 py-3 text-xs text-foreground-600">{totalWeeklyHours} h / semaine</td>
                <td className="px-5 py-3" />
                <td className="px-5 py-3 text-right font-semibold text-foreground-950">
                  {formatMoney(payroll)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <Pagination pager={page} className="border-t border-background-200" />
      </Card>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        {cycleSummary.map((cycle) => (
          <Card key={cycle.id}>
            <SectionTitle
              icon={cycle.id === PRIMARY_CYCLE_ID ? "ri-book-open-line" : "ri-community-line"}
              title={`Cycle ${cycle.label}`}
              subtitle={`${plural(cycle.count, "enseignant")} · ${cycle.hours} h / semaine`}
            />
            <div className="flex items-end justify-between">
              <span className="text-xs text-foreground-500">Masse salariale mensuelle</span>
              <span className="text-lg font-semibold font-heading text-foreground-950">
                {formatMoney(cycle.payroll)}
              </span>
            </div>
          </Card>
        ))}
      </div>

      <PayrollPrintModal
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        teachers={teachers}
        cycles={cycles}
      />
    </div>
  );
}